// Module: screenshotJpegEncoder
// -----------------------------------------------------------------
// Receives:  a finished screenshot canvas
// Produces:  its JPEG as a data URL
// Owns:      the one encode worker and the requests in flight to it
// Does NOT:  compose the screenshot (→ rasterize) or encode itself, except
//            on the main-thread fallback
// -----------------------------------------------------------------
//
// Anything the worker path cannot do — no Worker or OffscreenCanvas, a worker
// that fails to start, crashes or stays silent — falls back to encoding on the
// main thread.

import { FileHelpers } from "tldraw";

export interface JpegEncodeRequest {
  request_id: number;
  bitmap: ImageBitmap;
  quality: number;
}

export type JpegEncodeResponse =
  | { request_id: number; data_url: string }
  | { request_id: number; error: string };

// Chrome's default for canvas JPEG, stated so both paths agree everywhere.
const JPEG_QUALITY = 0.92;

// A worker that never answers (suspended tab, wedged thread) must not hold
// the turn; past this the frame is encoded on the main thread instead.
const WORKER_REPLY_TIMEOUT_MS = 5_000;

interface PendingEncode {
  resolve: (data_url: string) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface ScreenshotJpegEncoder {
  encode(canvas: HTMLCanvasElement): Promise<string>;
}

/**
 * Input:   createWorker — returns the encode worker, or null where workers
 *          are unavailable (injected so tests can supply a fake)
 * Output:  an encoder; the worker is created on first use
 */
export function createScreenshotJpegEncoder(
  createWorker: () => Worker | null,
): ScreenshotJpegEncoder {
  let worker: Worker | null = null;
  let is_worker_broken = false;
  let next_request_id = 1;
  const pending = new Map<number, PendingEncode>();

  const _rejectAll = (reason: string) => {
    for (const [, request] of pending) {
      clearTimeout(request.timer);
      request.reject(new Error(reason));
    }
    pending.clear();
  };

  // A worker that crashed or went silent is not trusted with another frame:
  // waiting out the timeout on every screenshot would be worse than no worker.
  const _retireWorker = (reason: string) => {
    is_worker_broken = true;
    worker?.terminate();
    worker = null;
    _rejectAll(reason);
  };

  const _startWorker = (): Worker | null => {
    if (worker || is_worker_broken) return worker;
    try {
      worker = createWorker();
    } catch {
      worker = null;
    }
    if (!worker) {
      is_worker_broken = true;
      return null;
    }
    worker.onmessage = (event: MessageEvent<JpegEncodeResponse>) => {
      const response = event.data;
      const request = pending.get(response.request_id);
      if (!request) return;
      pending.delete(response.request_id);
      clearTimeout(request.timer);
      if ("data_url" in response) request.resolve(response.data_url);
      else request.reject(new Error(response.error));
    };
    // A worker-level failure poisons the worker, not one frame.
    worker.onerror = () => _retireWorker("screenshot encode worker failed");
    return worker;
  };

  const _encodeInWorker = async (
    activeWorker: Worker,
    canvas: HTMLCanvasElement,
  ): Promise<string> => {
    const bitmap = await createImageBitmap(canvas);
    const request_id = next_request_id++;
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(
        () => _retireWorker("screenshot encode worker timed out"),
        WORKER_REPLY_TIMEOUT_MS,
      );
      pending.set(request_id, { resolve, reject, timer });
      const request: JpegEncodeRequest = {
        request_id,
        bitmap,
        quality: JPEG_QUALITY,
      };
      try {
        activeWorker.postMessage(request, [bitmap]);
      } catch (error) {
        // Not transferred, so still ours to release.
        clearTimeout(timer);
        pending.delete(request_id);
        bitmap.close();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  };

  return {
    /**
     * Input:   the finished screenshot canvas
     * Output:  its JPEG as a data URL
     * Mutates: starts the worker on first call
     */
    async encode(canvas) {
      const activeWorker = _startWorker();
      if (activeWorker) {
        try {
          return await _encodeInWorker(activeWorker, canvas);
        } catch (error) {
          console.warn(
            "[screenshotJpegEncoder] worker encode failed, encoding on the main thread",
            error,
          );
        }
      }
      return encodeOnMainThread(canvas);
    },
  };
}

/** Main-thread encode; the fallback. */
async function encodeOnMainThread(canvas: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
  return FileHelpers.blobToDataUrl(blob);
}

function _createDefaultWorker(): Worker | null {
  if (
    typeof Worker === "undefined" ||
    typeof OffscreenCanvas === "undefined" ||
    typeof createImageBitmap === "undefined"
  ) {
    return null;
  }
  return new Worker(
    new URL("./screenshotJpegEncodeWorker.ts", import.meta.url),
    { type: "module" },
  );
}

/** The app's encoder; one worker per tab, shared by every screenshot. */
export const screenshotJpegEncoder =
  createScreenshotJpegEncoder(_createDefaultWorker);
